import { useState, useRef } from "react";
import { mockStories } from "@/mocks/social";

interface CreatePostPanelProps {
  onShareProduct: () => void;
}

const tabs = [
  { id: "life", label: "Life Photo", icon: "ri-camera-line" },
  { id: "product", label: "Share & Earn", icon: "ri-money-dollar-circle-line" },
];

export default function CreatePostPanel({ onShareProduct }: CreatePostPanelProps) {
  const [activeTab, setActiveTab] = useState("life");
  const [postText, setPostText] = useState("");
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handlePost = () => {
    if (!postText.trim()) return;
    setPosted(true);
    setPostText("");
    setPreviewImg(null);
    setTimeout(() => setPosted(false), 3000);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4">
      {/* Stories Row */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-50">
        <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
          {mockStories.map((story) => (
            <div key={story.id} className="flex flex-col items-center gap-1.5 flex-shrink-0 cursor-pointer group">
              <div
                className={`w-14 h-14 rounded-full overflow-hidden border-2 transition-all group-hover:scale-105 ${
                  story.isAdd
                    ? "border-dashed border-orange-300 bg-orange-50 flex items-center justify-center"
                    : "border-orange-400"
                }`}
              >
                {story.isAdd ? (
                  <i className="ri-add-line text-orange-400 text-xl" />
                ) : (
                  <img src={story.avatar} alt={story.user} className="w-full h-full object-cover object-top" />
                )}
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">{story.user}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Switch */}
      <div className="flex border-b border-gray-100">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-colors cursor-pointer ${
              activeTab === tab.id
                ? "text-orange-500 border-b-2 border-orange-500 bg-orange-50"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Post Area */}
      <div className="p-4">
        {activeTab === "life" ? (
          <>
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-400 flex-shrink-0">
                <i className="ri-user-line" />
              </div>
              <div className="flex-1">
                <textarea
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder="Share your life moments with the StarBookPrime community..."
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 resize-none transition-colors"
                  rows={3}
                />
                <div className="text-xs text-gray-400 text-right mt-1">{postText.length}/500</div>

                {previewImg && (
                  <div className="relative mt-2 rounded-xl overflow-hidden w-full h-40">
                    <img src={previewImg} alt="preview" className="w-full h-full object-cover object-top" />
                    <button
                      onClick={() => setPreviewImg(null)}
                      className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/50 text-white rounded-full cursor-pointer"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-500 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap border border-gray-200"
                    >
                      <i className="ri-image-line" /> Photo
                    </button>
                    <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-500 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap border border-gray-200">
                      <i className="ri-video-line" /> Video
                    </button>
                    <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-500 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap border border-gray-200">
                      <i className="ri-emotion-line" /> Feeling
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" />
                  </div>
                  <button
                    onClick={handlePost}
                    disabled={!postText.trim()}
                    className="px-6 py-2 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-3xl text-amber-600" />
            </div>
            <h3
              className="text-xl font-extrabold text-gray-900 mb-2"
              style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
            >
              Share Products & Earn Commission
            </h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5 leading-relaxed">
              Pick any verified factory product, share it to your YouTube, TikTok, or Instagram fans, and earn real commission on every sale — helping your family live a better life.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onShareProduct}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-all hover:scale-105 cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-store-2-line" /> Pick a Product to Share
              </button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-5 text-gray-400 text-xs">
              <div className="flex items-center gap-1">
                <i className="ri-youtube-line text-red-500 text-base" /> YouTube
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-tiktok-line text-gray-900 text-base" /> TikTok
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-instagram-line text-pink-500 text-base" /> Instagram
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-facebook-circle-line text-amber-700 text-base" /> Facebook
              </div>
            </div>
          </div>
        )}

        {posted && (
          <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 rounded-xl px-4 py-3 text-sm font-semibold">
            <i className="ri-checkbox-circle-fill" />
            Your post has been shared with the community!
          </div>
        )}
      </div>
    </div>
  );
}
